import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Table, Form, Modal, Badge, Spinner, Alert, Pagination } from 'react-bootstrap';
import apiService from '../services/api';

const Countries = () => {
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingCountry, setEditingCountry] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [saving, setSaving] = useState(false);

    // Sorting state
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        code: '',
    });

    useEffect(() => {
        loadCountries();
    }, [currentPage, itemsPerPage]);

    const loadCountries = async () => {
        setLoading(true);
        setError('');

        try {
            const response = await apiService.getCountries();
            if (response.success) {
                setCountries(response.data || []);
                setTotalPages(Math.ceil((response.data?.total || response.data?.length || 0) / itemsPerPage));
            } else {
                setError(response.error || 'Failed to load countries');
            }
        } catch (error) {
            console.error('Error loading countries:', error);
            setError('Failed to load countries');
        } finally {
            setLoading(false);
        }
    };

    const handleShowModal = (country = null) => {
        if (country) {
            setEditingCountry(country);
            setFormData({
                name: country.name || '',
                code: country.code || '',
            });
        } else {
            setEditingCountry(null);
            setFormData({
                name: '',
                code: '',
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingCountry(null);
        setFormData({
            name: '',
            code: '',
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSave = async (e) => {
        e.preventDefault();

        setSaving(true);
        setError('');

        try {
            const countryData = {
                name: formData.name.trim(),
                code: formData.code.trim(),
            };

            let response;
            if (editingCountry) {
                response = await apiService.updateCountry(editingCountry.id, countryData);
            } else {
                response = await apiService.createCountry(countryData);
            }

            if (response.success) {
                handleCloseModal();
                await loadCountries();
            } else {
                setError(response.error || 'Failed to save country');
            }
        } catch (error) {
            console.error('Error saving country:', error);
            setError('Failed to save country');
        } finally {
            setSaving(false);
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(parseInt(e.target.value));
        setCurrentPage(1);
    };

    // Sorting functionality
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return <i className="bi bi-arrow-down-up text-muted ms-1"></i>;
        }
        return sortConfig.direction === 'asc'
            ? <i className="bi bi-arrow-up text-primary ms-1"></i>
            : <i className="bi bi-arrow-down text-primary ms-1"></i>;
    };

    // Sort countries based on current sort configuration
    const sortedCountries = React.useMemo(() => {
        let sortableCountries = [...countries];
        if (sortConfig.key) {
            sortableCountries.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                // Handle different data types
                switch (sortConfig.key) {
                    case 'name':
                    case 'code':
                        aValue = (aValue || '').toString().toLowerCase();
                        bValue = (bValue || '').toString().toLowerCase();
                        break;
                    case 'created_at':
                    case 'last_updated_at':
                        aValue = new Date(aValue || 0);
                        bValue = new Date(bValue || 0);
                        break;
                    default:
                        aValue = aValue || '';
                        bValue = bValue || '';
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableCountries;
    }, [countries, sortConfig]);

    return (
        <div>
            <Row>
                <Col>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h2>
                            <i className="bi bi-globe me-2"></i>
                            Countries
                        </h2>
                        <Button variant="primary" onClick={() => handleShowModal()}>
                            <i className="bi bi-plus-circle me-2"></i>
                            Add Country
                        </Button>
                    </div>
                </Col>
            </Row>

            {error && (
                <Row className="mb-4">
                    <Col>
                        <Alert variant="danger" dismissible onClose={() => setError('')}>
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            {error}
                        </Alert>
                    </Col>
                </Row>
            )}

            {/* Filter Section */}
            <Row className="mb-4">
                <Col md={6}>
                    <Form.Group>
                        <Form.Label>Items per page</Form.Label>
                        <Form.Select
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                        >
                            <option value="10">10 per page</option>
                            <option value="25">25 per page</option>
                            <option value="50">50 per page</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>

            <Row>
                <Col>
                    <Card>
                        <Card.Body className="p-0">
                            {loading ? (
                                <div className="text-center p-4">
                                    <Spinner animation="border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </Spinner>
                                </div>
                            ) : countries.length === 0 ? (
                                <div className="text-center py-4 text-muted">
                                    <i className="bi bi-globe display-1"></i>
                                    <div className="mt-2">No countries found</div>
                                </div>
                            ) : (
                                <Table responsive hover className="mb-0">
                                    <thead>
                                        <tr>
                                            <th
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                onClick={() => handleSort('name')}
                                            >
                                                Country Name
                                                {getSortIcon('name')}
                                            </th>
                                            <th
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                onClick={() => handleSort('code')}
                                            >
                                                Country Code
                                                {getSortIcon('code')}
                                            </th>
                                            <th
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                onClick={() => handleSort('created_at')}
                                            >
                                                Created
                                                {getSortIcon('created_at')}
                                            </th>
                                            <th
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                onClick={() => handleSort('last_updated_at')}
                                            >
                                                Updated
                                                {getSortIcon('last_updated_at')}
                                            </th>

                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedCountries.map((country) => (
                                            <tr key={country.id}>
                                                <td>
                                                    <div className="fw-medium">{country.name}</div>
                                                </td>
                                                <td>
                                                    <code>{country.code}</code>
                                                </td>
                                                <td>{country.created_at ? apiService.formatDate(country.created_at) : '-'}</td>
                                                <td>{country.last_updated_at ? apiService.formatDate(country.last_updated_at) : '-'}</td>

                                                <td>
                                                    <div className="d-flex gap-1">
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            onClick={() => handleShowModal(country)}
                                                            title="Edit"
                                                        >
                                                            <i className="bi bi-pencil"></i>
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <Card.Footer>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="text-muted">
                                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, countries.length)} of {countries.length} entries
                                    </div>
                                    <Pagination className="mb-0">
                                        <Pagination.Prev
                                            disabled={currentPage === 1}
                                            onClick={() => handlePageChange(currentPage - 1)}
                                        />
                                        {[...Array(totalPages)].map((_, i) => (
                                            <Pagination.Item
                                                key={i + 1}
                                                active={i + 1 === currentPage}
                                                onClick={() => handlePageChange(i + 1)}
                                            >
                                                {i + 1}
                                            </Pagination.Item>
                                        ))}
                                        <Pagination.Next
                                            disabled={currentPage === totalPages}
                                            onClick={() => handlePageChange(currentPage + 1)}
                                        />
                                    </Pagination>
                                </div>
                            </Card.Footer>
                        )}
                    </Card>
                </Col>
            </Row>

            {/* Create/Edit Modal */}
            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingCountry ? 'Edit Country' : 'Add Country'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSave}>
                    <Modal.Body>
                        <Row>
                            <Col md={8}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Country Name *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="e.g. United States"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Country Code *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="code"
                                        value={formData.code}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="e.g. US"
                                    />
                                    <Form.Text className="text-muted">
                                        ISO country code (e.g. US, GB, ZA)
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={saving}>
                            {saving ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                                    Saving...
                                </>
                            ) : (
                                'Save Country'
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default Countries;
